import React, { useContext, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core';
import { DialogResponse, DialogResponseCode, DialogType, hideSlideOutPanel, modeUIContext, showDialog, showSuccessNotification }
    from '@moderepo/mode-ui-state';
import { deleteHomeKVStore, userAppDataContext, selectHomeKVStore } from '@moderepo/mode-data-state-user-app';
import { BaseCompAction, capitalizeAll, SlideOutComp } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { useRitzTranslation } from '../../utils/customHooks';
import { HomeKVPairInfoContainer } from './HomeKVPairInfoContainer';


const useStyle = makeStyles((theme: Theme) => {
    return {
        primaryAction: {
            '&.remove:not(:disabled)': {
                background: theme.palette.secondary.main,
            },
        },
    };
});


interface HomeKVPairInfoPreviewContainerProps {
    readonly projectId: number;
    readonly kvPairKey: string | undefined;  // kvKey is undefined when we are in Create KV pair mode. NOTE: we can't use 'key' because it is reserved
    readonly homeId: number;
}


/**
 * This Component is used for displaying KVPairInfoContainer on the preview panel for preview.
 */
export const HomeKVPairInfoPreviewContainer = (props: HomeKVPairInfoPreviewContainerProps) => {
    const { projectId } = props;
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, projectId);
    const { trans } = useRitzTranslation(project);
    const isNewKV = props.kvPairKey === undefined;
    const selectedKVPair = props.kvPairKey ? selectHomeKVStore(appDataState, props.homeId, props.kvPairKey) : undefined;
    const classes = useStyle();



    /**
     * Unselect the selected KV pair and update the URL
     */
    const unselectKVPairEntity = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
    }, [uiDispatch]);



    /**
     * On create new KV Pair canceled event from KVPairInfoContainer we need to unselected the selected entity to close the preview panel
     */
    const onNewKVPairCanceled = useCallback(() => {
        unselectKVPairEntity();
    }, [unselectKVPairEntity]);



    /**
     * On create new KV Pair success, just hide the preview panel
     */
    const onNewKVPairCreated = useCallback(() => {
        unselectKVPairEntity();
    }, [unselectKVPairEntity]);



    /**
     * use useMemo to create the the newKVPairSettings so that this doesn't get created on every re-render
     */
    const newKVPairSettings = useMemo(() => {
        return {
            onNewKVPairCreated,
            onNewKVPairCanceled,
        };
    }, [onNewKVPairCreated, onNewKVPairCanceled]);


    const onDeleteKVPairActionClicked = useCallback((key: string) => {
        uiDispatch(showDialog({
            type          : DialogType.CONFIRM,
            title         : 'dialog.confirm_remove_kv_pair.title',
            message       : 'dialog.confirm_remove_kv_pair.message',
            messageData   : { key },
            positiveButton: 'label.delete',
            negativeButton: 'label.cancel',
            onClose       : async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    appDataDispatch(deleteHomeKVStore(props.homeId, key)).then(() => {
                        uiDispatch(showSuccessNotification('notif.generic_item_removed_successfully', { term: trans('label.kv_pair') }));
                        unselectKVPairEntity();
                    }).catch(() => {
                        // ignore error
                    });
                }
            },
        }));
    }, [uiDispatch, appDataDispatch, props.homeId, trans, unselectKVPairEntity]);


    const primaryActions = useMemo(() => {
        if (props.kvPairKey && selectedKVPair) {
            const actions: BaseCompAction[] = [{
                className: clsx(classes.primaryAction, 'remove'),
                icon     : 'delete',
                label    : capitalizeAll(trans('label.remove_kv_pair')),
                onClick  : () => {
                    onDeleteKVPairActionClicked(props.kvPairKey as string);
                },
            }];
            return actions;
        }
        return undefined;
    }, [classes.primaryAction, onDeleteKVPairActionClicked, props.kvPairKey, selectedKVPair, trans]);


    const slideOutCompContent = useMemo(() => {
        return (
            <HomeKVPairInfoContainer
                className="transparent"
                infoCompProps={{
                    formProps: {
                        className: 'single-column',
                    },
                }}
                projectId={projectId}
                homeId={props.homeId}
                kvPairKey={props.kvPairKey}
                newKVPairSettings={
                    isNewKV ? newKVPairSettings : undefined
                }
            />
        );
    }, [isNewKV, newKVPairSettings, projectId, props.homeId, props.kvPairKey]);


    return (
        <SlideOutComp
            primaryActions={primaryActions}
            compContent={slideOutCompContent}
            closable={!isNewKV}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
